import { Fragment, useEffect, useState } from "react";
import PageLayout from "../Layouts/PageLayout";
import Spinner from "../FieldComponets/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { PAGES } from "../../Constants/CommonConstants";
import { MaterialReactTable } from "material-react-table";
import { paginationQuery } from "../../utils/GlobalHelper";
import type { PaginationState } from "@tanstack/react-table";
import React from "react";
import { Link } from "react-router-dom";
import {
  SubscriptionPaymentListAction,
  SubscriptionPaymentApproveAction,
  SubscriptionPaymentRejectAction,
} from "../../Redux/Action/SubscriptionPaymentAction";
import Swal from "sweetalert2";
import { url } from "../../Config/Config";
import { encodeID, getDate } from "../../utils/Helper";

function SubscriptionPaymentRequestsList() {
  const dispatch = useDispatch<any>();
  const listState = useSelector((state: any) => state.SubscriptionPaymentListState);
  const approveSt = useSelector((state: any) => state.SubscriptionPaymentApproveState);
  const rejectSt = useSelector((state: any) => state.SubscriptionPaymentRejectState);

  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PAGES?.pageIndex,
    pageSize: PAGES?.pageSize,
  });
  const [sorting] = useState<any>(["DESC"]);
  const [rowCount, setRowCount] = useState(0);

  const fetchList = () => {
    const query: any = paginationQuery(
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      "",
      [],
    );
    query.status_filter = 0; /* pending only — matches API default */
    dispatch(SubscriptionPaymentListAction(query));
  };

  useEffect(() => {
    setLoading(true);
    fetchList();
    setLoading(false);
  }, [pagination.pageIndex, pagination.pageSize, approveSt.status, rejectSt.status]);

  useEffect(() => {
    setData(listState?.data?.data?.rows);
    setRowCount(listState?.data?.data?.count);
  }, [listState?.data]);

  const onApprove = (row: any) => {
    Swal.fire({
      title: "Approve subscription payment?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(SubscriptionPaymentApproveAction({ request_id: row.id }));
      }
    });
  };

  const onReject = (row: any) => {
    Swal.fire({
      title: "Reject request",
      input: "textarea",
      inputLabel: "Remark (optional)",
      showCancelButton: true,
      confirmButtonText: "Reject",
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(
          SubscriptionPaymentRejectAction({
            request_id: row.id,
            admin_remark: r.value || "",
          }),
        );
      }
    });
  };

  const columns: any = [
    {
      accessorKey: "S.NO",
      header: "S.No",
      accessorFn: (_row: object, index: number) =>
        pagination.pageIndex * pagination.pageSize + index + 1,
      size: 50,
      enableSorting: false,
    },
    {
      header: "Salon name",
      accessorFn: (row: any) => {
        const name = row?.salon?.salon_name || "—";
        const sid = row?.salon?.id;
        if (!sid || name === "—") return name;
        return (
          <Link to={`${url}/salon-details/${encodeID(sid)}`} className="fw-semibold text-decoration-none">
            {name}
          </Link>
        );
      },
      enableSorting: false,
    },
    {
      header: "Salon email",
      accessorFn: (row: any) => row?.salon?.email || "—",
      enableSorting: false,
    },
    {
      header: "Subscription plan type",
      accessorFn: (row: any) => row?.subscription_plan_type || "—",
      enableSorting: false,
    },
    {
      header: "Subscription purchase date",
      accessorFn: (row: any) =>
        row?.subscription_purchase_date
          ? getDate(row.subscription_purchase_date)
          : "—",
      enableSorting: false,
    },
    {
      header: "Subscription active date",
      accessorFn: (row: any) =>
        row?.subscription_active_date
          ? getDate(row.subscription_active_date)
          : "—",
      enableSorting: false,
    },
    {
      header: "Subscription suspended",
      accessorFn: (row: any) =>
        row?.subscription_suspended_at
          ? getDate(row.subscription_suspended_at)
          : "—",
      enableSorting: false,
    },
    {
      header: "Subscription status",
      accessorFn: (row: any) => row?.subscription_status_label || "—",
      enableSorting: false,
    },
    {
      header: "Requested plan",
      accessorFn: (row: any) => row?.plan?.name || "—",
      enableSorting: false,
    },
    {
      accessorKey: "transaction_id",
      header: "Transaction ID",
      enableSorting: false,
    },
    {
      header: "Proof",
      accessorFn: (row: any) =>
        row.payment_proof_url ? (
          <a href={row.payment_proof_url} target="_blank" rel="noreferrer">
            View
          </a>
        ) : (
          "—"
        ),
      enableSorting: false,
    },
    {
      header: "Action",
      accessorFn: (row: any) => (
        <div className="btn-group">
          <button type="button" className="btn btn-success btn-sm me-1" onClick={() => onApprove(row)}>
            Approve
          </button>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => onReject(row)}>
            Reject
          </button>
        </div>
      ),
      enableSorting: false,
    },
  ];

  return (
    <Fragment>
      {(loading || listState?.loading) ? <Spinner /> : ""}
      <PageLayout
        title="Pending subscription payments"
        breadcrumbs={{
          secLast: { link: "/subscription-payment-requests", name: "Subscription payments" },
        }}
        buttons={[
          {
            url: "/subscription-transaction-history",
            className: "btn btn-outline-primary me-2",
            text: "Full history",
            show: true,
          },
          {
            url: "/offline-bank-settings",
            className: "btn btn-primary",
            text: "Bank settings",
            show: true,
          },
        ]}
      >
        {data && (
          <MaterialReactTable
            columns={columns}
            data={data}
            enableGlobalFilter={false}
            enableColumnFilters={false}
            manualPagination
            rowCount={rowCount}
            onPaginationChange={setPagination}
            state={{ pagination }}
          />
        )}
      </PageLayout>
    </Fragment>
  );
}

export default SubscriptionPaymentRequestsList;
